import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import {
  businessLineAllocationModeOptions,
  businessLineFieldTooltips,
  businessLineMaxInterventionsOptions,
  businessLineModelOptions,
} from '@constants/businessLines.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import {
  EBusinessLineAllocationMode,
  EBusinessLineModel,
} from '@enums/businessLine.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import {
  createBusinessLineSchema,
  type CreateBusinessLinePayload,
} from '@services/businessLines/dtos/create.dto';
import { businessLineService } from '@services/businessLines/service';
import type { CreateBusinessLineOutput } from '@services/businessLines/types/create.type';
import { workflowService } from '@services/workflows/service';

import { BusinessLineIconPicker } from './@components/IconPicker';
import { BusinessLineServiceTypes } from './@components/ServiceTypes';

export const Route = createFileRoute(
  '/_layout/(private)/business-lines/create',
)({
  component: BusinessLineCreate,
});

function BusinessLineCreate() {
  const navigate = Route.useNavigate();
  const { queryClient } = Route.useRouteContext();

  const { data: workflowOptions, isFetching: isWorkflowsFetching } = useQuery({
    queryKey: queryKeys.workflows.select,
    queryFn: () => workflowService.getAllToSelect(),
    staleTime: EStaleTime.OneDay,
  });

  const form = useForm<CreateBusinessLinePayload>({
    resolver: zodResolver(createBusinessLineSchema),
    mode: 'onBlur',
    defaultValues: {
      maxInterventions: -1,
      name: '',
      translations: { en: '', fr: '', nl: '' },
      allowedServiceTypeIds: [],
      color: '#000000',
      allocationMode: EBusinessLineAllocationMode.Automatic,
      model: EBusinessLineModel.Multiple,
      workflowId: '',
      emailBranding: '',
      icon: 'CircleQuestionMarkIcon',
    },
  });

  const { isPending, mutate: createBusinessLine } = useMutation<
    CreateBusinessLineOutput,
    unknown,
    CreateBusinessLinePayload
  >({
    mutationFn: businessLineService.create,
    onError: handleRequestError,
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.businessLines.all });
      navigate({
        to: '/business-lines/$id',
        params: { id },
        replace: true,
      });
      toast.success('Success', {
        description: 'Business line created successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    createBusinessLine(values);
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Create Business Line" />

      <form
        className="mx-auto mt-5 space-y-3 p-2 md:w-4/5 2xl:w-3/5"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <InputControlled
            label="Name"
            name="name"
            tooltip={businessLineFieldTooltips.name}
          />
          <InputControlled
            label="Translation (EN)"
            name="translations.en"
            tooltip={businessLineFieldTooltips.translationEn}
          />
          <InputControlled
            label="Translation (FR)"
            name="translations.fr"
            tooltip={businessLineFieldTooltips.translationFr}
          />
          <InputControlled
            label="Translation (NL)"
            name="translations.nl"
            tooltip={businessLineFieldTooltips.translationNl}
          />
        </div>

        <Separator variant="form" />

        <div className="grid grid-cols-2 gap-2 xl:grid-cols-[repeat(2,0.5fr)_repeat(2,1fr)]">
          <InputControlled
            label="Model"
            name="model"
            options={businessLineModelOptions}
            tooltip={businessLineFieldTooltips.model}
            variant="select"
          />
          <InputControlled
            label="Allocation mode"
            name="allocationMode"
            options={businessLineAllocationModeOptions}
            tooltip={businessLineFieldTooltips.allocationMode}
            variant="select"
          />
          <InputControlled
            disabled={isWorkflowsFetching}
            label="Workflow"
            name="workflowId"
            options={workflowOptions?.list ?? []}
            tooltip={businessLineFieldTooltips.workflow}
            variant="select"
          />
          <InputControlled
            label="Interventions per s. requests"
            name="maxInterventions"
            options={businessLineMaxInterventionsOptions}
            tooltip={businessLineFieldTooltips.maxInterventions}
            variant="select"
          />
        </div>

        <Separator variant="form" />

        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-[repeat(2,0.15fr)_1fr_0.5fr]">
          <InputControlled label="Color" name="color" type="color" />
          <BusinessLineIconPicker />

          <InputControlled
            label="Email branding"
            name="emailBranding"
            tooltip={businessLineFieldTooltips.emailBranding}
          />

          <BusinessLineServiceTypes />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            rounded="lg"
            size="lg"
            to=".."
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
